import React, { useEffect } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { trackEvent } from "../lib/track";

const LOGO = "/assets/logo.png";

/**
 * Branded 404 — never leaves the visitor stranded.
 * Includes a clear primary "Return to the Homepage" CTA plus a few
 * common next-step destinations so a mistyped URL never becomes a
 * dead end in the conversion funnel.
 */
const NotFoundPage = () => {
  useEffect(() => {
    trackEvent("page_not_found", { path: window.location.pathname });
  }, []);

  return (
    <div
      data-testid="not-found-page"
      className="min-h-screen bg-themed text-themed relative overflow-hidden flex items-center justify-center px-6"
    >
      <div className="absolute inset-0 gold-glow-radial pointer-events-none opacity-60" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <div className="relative max-w-2xl text-center py-20">
        <div className="flex justify-center mb-10">
          <img
            src={LOGO}
            alt="Authentic Momentum"
            className="w-24 h-24 object-contain logo-mask animate-breathe-premium"
            draggable="false"
          />
        </div>

        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
          Off-Path · Page Not Found
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl text-themed leading-tight font-light">
          This path is not yet written.
        </h1>
        <p className="mt-6 text-themed-muted leading-relaxed max-w-md mx-auto">
          The page you are looking for either moved, was renamed, or never
          existed. Your momentum, however, is still here.
        </p>

        <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/"
            data-testid="not-found-home"
            className="btn-gold inline-flex items-center gap-2 justify-center"
          >
            <ArrowLeft size={13} /> Return to the Homepage
          </a>
          <a
            href="/frequency-snapshot"
            data-testid="not-found-snapshot"
            className="btn-ghost-gold inline-flex items-center gap-2 justify-center"
          >
            Take the Clarity Snapshot <ArrowRight size={13} />
          </a>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-md mx-auto">
          <a
            href="/book"
            className="text-[10px] tracking-[0.3em] uppercase text-themed-muted hover:text-gold border border-white/10 hover:border-gold/40 rounded-full px-4 py-3 transition-colors"
            data-testid="not-found-book"
          >
            The Book
          </a>
          <a
            href="/?checkout=blueprint"
            className="text-[10px] tracking-[0.3em] uppercase text-themed-muted hover:text-gold border border-white/10 hover:border-gold/40 rounded-full px-4 py-3 transition-colors"
            data-testid="not-found-blueprint"
          >
            The Personal Action Plan
          </a>
          <a
            href="/journal"
            className="text-[10px] tracking-[0.3em] uppercase text-themed-muted hover:text-gold border border-white/10 hover:border-gold/40 rounded-full px-4 py-3 transition-colors"
            data-testid="not-found-journal"
          >
            The Journal
          </a>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
